import { Component, HostListener, OnInit } from '@angular/core';
import { OrganizationConfig } from '../../../../../models/organization-config';
import { AccountService } from '../../../../../services/account/account.service';
import { OrganizationConfigService } from '../../../../../services/organization-config/organization-config.service';
import { BuiltInCheckInSound } from '../../../../../models/check-in-sound';
import { CheckInSoundService } from '../../../../../services/check-in-sound/check-in-sound.service';
import { ComponentHasUnsavedChanges } from '../../../../../unsaved-changes-guard.service';


interface CheckInSoundItem {
  name: string;
  fieldName: string;
  helpText: string;
}

@Component({
  selector: 'app-check-in-sounds-config',
  templateUrl: './check-in-sounds-config.component.html',
  styleUrls: ['./check-in-sounds-config.component.css']
})
export class CheckInSoundsConfigComponent implements OnInit, ComponentHasUnsavedChanges {

  public organizationConfig: OrganizationConfig;
  public builtInSounds: BuiltInCheckInSound[];

  public loadingOrganizationConfig = false;
  public loadingBuiltInSounds = false;
  public savingOrganizationConfig = false;

  public successMessage = '';
  public errorMessage = '';
  public errors: any = {};
  public unsavedChanges = false;

  public serverError: number = null;

  public readonly CheckInSoundItems: CheckInSoundItem[] = [
    {
      name: 'Check-In Success',
      fieldName: 'check_in_sound_success_id',
      helpText: 'This sound plays when a customer checks in successfully.',
    },
    {
      name: 'Check-In Success with Challenge Completed',
      fieldName: 'check_in_sound_success_with_challenge_completed_id',
      helpText: 'This sound plays when a customer checks in successfully, and they have completed a challenge with this check-in.',
    },
    {
      name: 'Check-In Success with Automatic Event Check-In',
      fieldName: 'check_in_sound_success_with_event_check_in_id',
      helpText: 'This sound plays when a customer checks in successfully, and they have an event reservation which has been automatically checked in.',
    },
    {
      name: 'Check-In Success During Association Re-verification Period',
      fieldName: 'check_in_sound_success_with_reverification_period_id',
      helpText: 'This sound plays when a customer checks in successfully during the re-verification period for one of their associations.',
    },
    {
      name: 'Check-In Success with Missing Proficiency',
      fieldName: 'check_in_sound_success_with_missing_proficiency_id',
      helpText: 'This sound plays when a customer checks in successfully at a location with required proficiencies ' +
        '(configured in Location Settings) without holding them. If set to "No Sound", the check-in plays whichever ' +
        'other success sound applies instead.',
    },
    {
      name: 'Check-In No Entry Method',
      fieldName: 'check_in_sound_no_entry_method_id',
      helpText: 'This sound plays when the customer cannot be checked in because they have no active membership or entry pass.',
    },
    {
      name: 'Check-In Account Alerts',
      fieldName: 'check_in_sound_alerts_id',
      helpText: 'This sound plays when the customer cannot be checked in due to outstanding alerts on their account (such as "no active waiver").',
    },
    {
      name: 'Check-In No Barcode Match',
      fieldName: 'check_in_sound_no_barcode_match_id',
      helpText: 'This sound plays when a customer account cannot be found for the scanned barcode.',
    },
    {
      name: 'Duplicate Check-In',
      fieldName: 'check_in_sound_duplicate_id',
      helpText: 'This sound plays when the customer cannot be checked in because they have already checked in in the last five minutes.',
    },
    {
      name: 'Check-Out Success',
      fieldName: 'check_out_sound_success_id',
      helpText: 'This sound plays when the customer checks out successfully.',
    },
    {
      name: 'Check-Out Failure',
      fieldName: 'check_out_sound_error_id',
      helpText: 'This sound plays when the customer cannot be checked out (for any reason).',
    },
  ];

  // Must mirror getSuccessSoundUrl() in the Check-In Site's main component.
  public readonly SuccessSoundPriority: CheckInSoundItem[] = [
    'check_in_sound_success_with_missing_proficiency_id',
    'check_in_sound_success_with_challenge_completed_id',
    'check_in_sound_success_with_event_check_in_id',
    'check_in_sound_success_with_reverification_period_id',
    'check_in_sound_success_id',
  ].map(fieldName => this.CheckInSoundItems.find(item => item.fieldName === fieldName));

  constructor(
    private accountService: AccountService,
    private organizationConfigService: OrganizationConfigService,
    private checkInSoundService: CheckInSoundService
  ) { }

  ngOnInit() {
    this.loadOrganizationConfig();
    this.loadBuiltInSounds();
  }

  hasUnsavedChanges() {
    return this.unsavedChanges;
  }

  @HostListener('window:beforeunload')
  allowUnload() {
    return !this.hasUnsavedChanges();
  }

  /**
   * Load the organization config from the API.
   *
   * We do not use the local-stored copy, in order to avoid pop-in of changed
   * values.
   */
  public loadOrganizationConfig() {
    this.loadingOrganizationConfig = true;

    // No catch on this call because failures are handled within
    // organizationConfigService.loadOrganizationConfig.
    this.organizationConfigService
      .loadOrganizationConfig()
      .then(() => {
        this.organizationConfig = this.organizationConfigService
          .getOrganizationConfig();
      })
      .finally(() => { this.loadingOrganizationConfig = false; });
  }

  /**
   * Note: The sound should be cached by the browser, so this will not require
   * a new load from the server each time the sound is played.
   */
  public playSound(soundId: number) {
    for (const builtInSound of this.builtInSounds) {
      if (builtInSound.id === soundId) {
        const audio = new Audio(builtInSound.sound_url);
        audio.play();
        return;
      }
    }
  }

  /**
   * Save the updated organization config to the API.
   */
  public saveOrganizationConfig() {
    this.savingOrganizationConfig = true;
    this.successMessage = '';

    this.errors = {};
    this.errorMessage = '';

    this.organizationConfigService
      .updateCheckInSoundConfig(this.organizationConfig)
      .then(updatedOrgConf => {
        this.unsavedChanges = false;
        this.organizationConfig = updatedOrgConf;
        this.successMessage = 'Check-in sounds configuration updated successfully.';
      })
      .catch(reason => {
        if (reason.status === 400) {
          this.errors = reason.error;
          this.errorMessage = 'Please correct the errors above and try again.';
        } else if (reason.status === 403) {
          this.errorMessage = 'You do not have permission to modify the check-in sounds configuration.';
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.savingOrganizationConfig = false; });
  }

  private loadBuiltInSounds() {
    this.loadingBuiltInSounds = true;

    this.checkInSoundService.getBuiltInCheckInSounds()
      .then(builtInSounds => {
        this.builtInSounds = builtInSounds;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingBuiltInSounds = false; });
  }

}
